import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  IconButton,
  Alert,
  CircularProgress,
  FormControlLabel,
  Autocomplete,
  Chip,
} from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { projectAPI } from '../services/api';

interface Activity {
  id: number;
  name: string;
  is_active: boolean;
  sort_order: number;
  is_billable: boolean;
}

interface ProjectActivitiesManagerProps {
  projectId: number;
  isTimeBased: boolean;
}

const ProjectActivitiesManager: React.FC<ProjectActivitiesManagerProps> = ({ projectId, isTimeBased }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedActivityIds, setSelectedActivityIds] = useState<number[]>([]);

  useEffect(() => {
    if (isTimeBased) {
      loadActivities();
    }
  }, [projectId, isTimeBased]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await projectAPI.getActivities(projectId);
      setActivities(response.data.data || []);
      // Set selected activities (billable ones)
      const billableIds = (response.data.data || [])
        .filter((a: Activity) => a.is_billable)
        .map((a: Activity) => a.id);
      setSelectedActivityIds(billableIds);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    // Set selected activities (billable ones)
    const billableIds = activities
      .filter((a) => a.is_billable)
      .map((a) => a.id);
    setSelectedActivityIds(billableIds);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      await projectAPI.updateActivity(projectId, { activity_ids: selectedActivityIds });
      await loadActivities();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save billable activities');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBillable = (activityId: number) => {
    setSelectedActivityIds(prev =>
      prev.includes(activityId)
        ? prev.filter(id => id !== activityId)
        : [...prev, activityId]
    );
  };

  if (!isTimeBased) {
    return null;
  }

  const billableActivities = activities.filter(a => a.is_billable);
  const availableActivities = activities.filter(a => a.is_active);

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: 'primary.main' }}>
          Billable Activities
        </Typography>
        <Button
          variant="contained"
          onClick={handleOpenDialog}
          size="small"
        >
          Manage Billable Activities
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && activities.length === 0 ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {billableActivities.length === 0 ? (
            <Alert severity="info">
              No billable activities selected. Click "Manage Billable Activities" to select which activities are billable for this project.
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {billableActivities.length} billable {billableActivities.length === 1 ? 'activity' : 'activities'} selected:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {billableActivities.map((activity) => (
                  <Chip
                    key={activity.id}
                    label={activity.name}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Manage Billable Activities</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which activities are billable for this project. Only selected activities will be available when stopping timers.
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {availableActivities.length === 0 ? (
                <Alert severity="warning">No active activities found.</Alert>
              ) : (
                availableActivities.map((activity) => (
                  <FormControlLabel
                    key={activity.id}
                    control={
                      <Checkbox
                        checked={selectedActivityIds.includes(activity.id)}
                        onChange={() => handleToggleBillable(activity.id)}
                      />
                    }
                    label={activity.name}
                    sx={{ display: 'block', mb: 0.5 }}
                  />
                ))
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ProjectActivitiesManager;
